/**
 * RecurrenceConfig — UI for configuring recurring job patterns.
 *
 * Supports: Daily, Weekly (1-99 week interval), Monthly (absolute day or relative weekday),
 * Yearly (absolute date or relative weekday), Custom (explicit dates).
 * Shows a live preview of generated dates (calls /api/jobs/recurring/preview).
 */
import { useState, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Calendar, X, Loader2, AlertCircle } from 'lucide-react';

const WEEKDAYS = [
  { value: 0, label: 'Mon' }, { value: 1, label: 'Tue' }, { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' }, { value: 4, label: 'Fri' }, { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

const ORDINALS = [
  { value: 1, label: '1st' }, { value: 2, label: '2nd' }, { value: 3, label: '3rd' },
  { value: 4, label: '4th' }, { value: -1, label: 'Last' },
];

const MONTHS_LIST = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => i + 1);
const INTERVALS = Array.from({ length: 99 }, (_, i) => i + 1);

export default function RecurrenceConfig({ startDate, onConfigChange, api, formatDateDisplay }) {
  const [pattern, setPattern] = useState('weekly');
  const [interval, setIntervalVal] = useState(1);
  const [weekdays, setWeekdays] = useState([]);
  const [monthDays, setMonthDays] = useState([]);
  const [monthlyMode, setMonthlyMode] = useState('absolute');
  const [ordinalWeeks, setOrdinalWeeks] = useState([]);
  const [relWeekdays, setRelWeekdays] = useState([]);
  const [yearMonth, setYearMonth] = useState(1);
  const [yearlyMode, setYearlyMode] = useState('absolute');
  const [customDates, setCustomDates] = useState([]);
  const [endType, setEndType] = useState('count');
  const [endDate, setEndDate] = useState('');
  const [repeatCount, setRepeatCount] = useState(4);

  const [previewDates, setPreviewDates] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const buildConfig = useCallback(() => {
    let effectivePattern = pattern;
    if (pattern === 'monthly' && monthlyMode === 'relative') effectivePattern = 'monthly_relative';
    if (pattern === 'yearly' && yearlyMode === 'relative') effectivePattern = 'yearly_relative';

    return {
      pattern: effectivePattern,
      interval: pattern === 'custom' ? 1 : interval,
      weekdays: effectivePattern === 'weekly' ? weekdays : [],
      month_days: (effectivePattern === 'monthly' || effectivePattern === 'yearly') ? monthDays : [],
      ordinal_weeks: (effectivePattern === 'monthly_relative' || effectivePattern === 'yearly_relative') ? ordinalWeeks : [],
      rel_weekdays: (effectivePattern === 'monthly_relative' || effectivePattern === 'yearly_relative') ? relWeekdays : [],
      year_month: (effectivePattern === 'yearly' || effectivePattern === 'yearly_relative') ? yearMonth : undefined,
      custom_dates: effectivePattern === 'custom' ? customDates : [],
      end_type: pattern === 'custom' ? 'count' : endType,
      end_date: endType === 'date' && pattern !== 'custom' ? endDate : undefined,
      repeat_count: (endType === 'count' || pattern === 'custom')
        ? (pattern === 'custom' ? customDates.length : repeatCount)
        : undefined,
    };
  }, [pattern, interval, weekdays, monthDays, monthlyMode, ordinalWeeks, relWeekdays, yearMonth, yearlyMode, customDates, endType, endDate, repeatCount]);

  useEffect(() => { onConfigChange(buildConfig()); }, [buildConfig, onConfigChange]);

  useEffect(() => {
    if (!startDate || !api) return;
    const config = buildConfig();
    const ep = config.pattern;

    if (ep === 'custom' && customDates.length === 0) { setPreviewDates([]); return; }
    if (ep === 'weekly' && weekdays.length === 0) { setPreviewDates([]); return; }
    if (ep === 'monthly' && monthDays.length === 0) { setPreviewDates([]); return; }
    if (ep === 'monthly_relative' && (!ordinalWeeks.length || !relWeekdays.length)) { setPreviewDates([]); return; }
    if (ep === 'yearly' && monthDays.length === 0) { setPreviewDates([]); return; }
    if (ep === 'yearly_relative' && (!ordinalWeeks.length || !relWeekdays.length)) { setPreviewDates([]); return; }
    if (endType === 'date' && !endDate && ep !== 'custom') { setPreviewDates([]); return; }
    if (endType === 'count' && (!repeatCount || repeatCount < 1) && ep !== 'custom') { setPreviewDates([]); return; }

    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const res = await api.post('/jobs/recurring/preview', { job: { start_date: startDate }, recurrence: config });
        setPreviewDates(res.data?.dates || []);
      } catch (err) {
        setPreviewError(err.response?.data?.detail || 'Failed to generate preview');
        setPreviewDates([]);
      } finally { setPreviewLoading(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [startDate, buildConfig, api, customDates, weekdays, monthDays, ordinalWeeks, relWeekdays, endType, endDate, repeatCount]);

  const toggleWeekday = (d) => setWeekdays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
  const toggleMonthDay = (d) => setMonthDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort((a, b) => a - b));
  const toggleOrdinal = (v) => setOrdinalWeeks(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v].sort((a, b) => (a < 0 ? 5 : a) - (b < 0 ? 5 : b)));
  const toggleRelWeekday = (d) => setRelWeekdays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
  const addCustomDate = (s) => { if (s && !customDates.includes(s)) setCustomDates(prev => [...prev, s].sort()); };
  const removeCustomDate = (s) => setCustomDates(prev => prev.filter(d => d !== s));

  const displayDate = (iso) => {
    if (formatDateDisplay) return formatDateDisplay(iso);
    try { return format(parseISO(iso), 'dd/MM/yyyy'); } catch { return iso; }
  };

  const intervalUnit = { daily: 'day', weekly: 'week', monthly: 'month', yearly: 'year' }[pattern] || '';

  return (
    <div className="space-y-4 p-4 rounded-lg border border-border bg-card/50" data-testid="recurrence-config">
      {/* Pattern Selector */}
      <div className="space-y-2">
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Recurrence Pattern</Label>
        <Select value={pattern} onValueChange={(v) => { setPattern(v); if (v === 'monthly') setMonthlyMode('absolute'); if (v === 'yearly') setYearlyMode('absolute'); }} data-testid="recurrence-pattern-select">
          <SelectTrigger data-testid="recurrence-pattern-trigger"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="yearly">Yearly</SelectItem>
            <SelectItem value="custom">Custom Dates</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Interval */}
      {pattern !== 'custom' && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Frequency</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Every</span>
            <Select value={String(interval)} onValueChange={v => setIntervalVal(parseInt(v))} data-testid="recurrence-interval-select">
              <SelectTrigger className="w-[72px] h-8" data-testid="recurrence-interval-trigger"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-[200px]">
                {INTERVALS.map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">{intervalUnit}{interval !== 1 ? 's' : ''}</span>
          </div>
        </div>
      )}

      {/* Weekly: Weekday Picker */}
      {pattern === 'weekly' && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Repeat on</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="weekday-picker">
            {WEEKDAYS.map(({ value, label }) => (
              <Button key={value} type="button" size="sm"
                variant={weekdays.includes(value) ? 'default' : 'outline'}
                className={`h-8 px-3 text-xs ${weekdays.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                onClick={() => toggleWeekday(value)} data-testid={`weekday-${label.toLowerCase()}`}>{label}</Button>
            ))}
          </div>
        </div>
      )}

      {/* Monthly */}
      {pattern === 'monthly' && (
        <div className="space-y-3">
          <div className="flex gap-1 p-0.5 bg-muted rounded-md w-fit" data-testid="monthly-mode-toggle">
            <button type="button" data-testid="monthly-mode-absolute"
              className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${monthlyMode === 'absolute' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setMonthlyMode('absolute')}>Day of month</button>
            <button type="button" data-testid="monthly-mode-relative"
              className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${monthlyMode === 'relative' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setMonthlyMode('relative')}>Specific weekday</button>
          </div>

          {monthlyMode === 'absolute' ? (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Day(s) of month</Label>
              <div className="flex flex-wrap gap-1" data-testid="monthday-picker">
                {MONTH_DAYS.map(day => (
                  <Button key={day} type="button" size="sm"
                    variant={monthDays.includes(day) ? 'default' : 'outline'}
                    className={`h-7 w-8 p-0 text-xs ${monthDays.includes(day) ? 'bg-primary text-primary-foreground' : ''}`}
                    onClick={() => toggleMonthDay(day)} data-testid={`monthday-${day}`}>{day}</Button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Position</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="ordinal-picker">
                  {ORDINALS.map(({ value, label }) => (
                    <Button key={value} type="button" size="sm"
                      variant={ordinalWeeks.includes(value) ? 'default' : 'outline'}
                      className={`h-8 px-3 text-xs ${ordinalWeeks.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                      onClick={() => toggleOrdinal(value)} data-testid={`ordinal-${label.toLowerCase()}`}>{label}</Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">On</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="rel-weekday-picker">
                  {WEEKDAYS.map(({ value, label }) => (
                    <Button key={value} type="button" size="sm"
                      variant={relWeekdays.includes(value) ? 'default' : 'outline'}
                      className={`h-8 px-3 text-xs ${relWeekdays.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                      onClick={() => toggleRelWeekday(value)} data-testid={`rel-weekday-${label.toLowerCase()}`}>{label}</Button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Yearly */}
      {pattern === 'yearly' && (
        <div className="space-y-3">
          <div className="flex gap-1 p-0.5 bg-muted rounded-md w-fit" data-testid="yearly-mode-toggle">
            <button type="button" data-testid="yearly-mode-absolute"
              className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${yearlyMode === 'absolute' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setYearlyMode('absolute')}>On date</button>
            <button type="button" data-testid="yearly-mode-relative"
              className={`px-3 py-1.5 text-xs rounded font-medium transition-colors ${yearlyMode === 'relative' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              onClick={() => setYearlyMode('relative')}>On weekday</button>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Month</Label>
            <Select value={String(yearMonth)} onValueChange={v => setYearMonth(parseInt(v))} data-testid="year-month-select">
              <SelectTrigger className="w-[160px]" data-testid="year-month-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS_LIST.map(({ value, label }) => <SelectItem key={value} value={String(value)}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {yearlyMode === 'absolute' ? (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Day(s) of month</Label>
              <div className="flex flex-wrap gap-1" data-testid="yearly-monthday-picker">
                {MONTH_DAYS.map(day => (
                  <Button key={day} type="button" size="sm"
                    variant={monthDays.includes(day) ? 'default' : 'outline'}
                    className={`h-7 w-8 p-0 text-xs ${monthDays.includes(day) ? 'bg-primary text-primary-foreground' : ''}`}
                    onClick={() => toggleMonthDay(day)} data-testid={`yearly-monthday-${day}`}>{day}</Button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Position</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="yearly-ordinal-picker">
                  {ORDINALS.map(({ value, label }) => (
                    <Button key={value} type="button" size="sm"
                      variant={ordinalWeeks.includes(value) ? 'default' : 'outline'}
                      className={`h-8 px-3 text-xs ${ordinalWeeks.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                      onClick={() => toggleOrdinal(value)} data-testid={`yearly-ordinal-${label.toLowerCase()}`}>{label}</Button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">On</Label>
                <div className="flex flex-wrap gap-1.5" data-testid="yearly-rel-weekday-picker">
                  {WEEKDAYS.map(({ value, label }) => (
                    <Button key={value} type="button" size="sm"
                      variant={relWeekdays.includes(value) ? 'default' : 'outline'}
                      className={`h-8 px-3 text-xs ${relWeekdays.includes(value) ? 'bg-primary text-primary-foreground' : ''}`}
                      onClick={() => toggleRelWeekday(value)} data-testid={`yearly-rel-weekday-${label.toLowerCase()}`}>{label}</Button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Custom */}
      {pattern === 'custom' && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Select dates</Label>
          <div className="flex items-center gap-2">
            <DatePicker value="" onChange={(val) => addCustomDate(val)} className="flex-1 h-8 text-sm" testId="custom-date-add" />
          </div>
          {customDates.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {customDates.map(d => (
                <Badge key={d} variant="secondary" className="gap-1 pr-1">
                  {displayDate(d)}
                  <button type="button" onClick={() => removeCustomDate(d)} className="ml-0.5 hover:text-destructive"><X className="h-3 w-3" /></button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      {/* End Type */}
      {pattern !== 'custom' && (
        <div className="space-y-2">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Ends</Label>
          <div className="flex items-center gap-3">
            <Select value={endType} onValueChange={setEndType} data-testid="recurrence-end-type">
              <SelectTrigger className="w-[140px]" data-testid="recurrence-end-type-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="date">On date</SelectItem>
                <SelectItem value="count">After</SelectItem>
              </SelectContent>
            </Select>
            {endType === 'date' ? (
              <DatePicker value={endDate} onChange={setEndDate} minDate={startDate} formatDisplay={formatDateDisplay} className="flex-1 h-8 text-sm" testId="recurrence-end-date" />
            ) : (
              <div className="flex items-center gap-2">
                <Input type="number" min={1} max={365} value={repeatCount}
                  onChange={(e) => setRepeatCount(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
                  className="w-20 h-8 text-sm" data-testid="recurrence-repeat-count" />
                <span className="text-sm text-muted-foreground">occurrences</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Preview */}
      <div className="space-y-2 pt-2 border-t border-border">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" /> Preview
            {previewDates.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">{previewDates.length} job{previewDates.length !== 1 ? 's' : ''}</Badge>
            )}
          </Label>
          {previewLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        {previewError && (
          <div className="flex items-center gap-1.5 text-xs text-destructive"><AlertCircle className="h-3.5 w-3.5" />{previewError}</div>
        )}
        {previewDates.length > 0 ? (
          <ScrollArea className="max-h-[160px] rounded border border-border">
            <div className="p-2 space-y-0.5">
              {previewDates.map((d, i) => (
                <div key={d} className="flex items-center gap-2 text-sm py-0.5 px-1.5 rounded hover:bg-muted/50">
                  <span className="text-[10px] text-muted-foreground w-5 text-right">{i + 1}.</span>
                  <span>{displayDate(d)}</span>
                  <span className="text-xs text-muted-foreground">
                    {(() => { try { return format(parseISO(d), 'EEEE'); } catch { return ''; } })()}
                  </span>
                </div>
              ))}
            </div>
          </ScrollArea>
        ) : (
          !previewLoading && !previewError && (
            <p className="text-xs text-muted-foreground italic">Configure pattern and end to preview dates</p>
          )
        )}
      </div>
    </div>
  );
}
